import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect, useRef } from "react";
import useYesNoOptions from "../../../../../hooks/useYesNoOptions";
import useReferralOptions from "../../../../../hooks/useReferralOptions";
import Input from "../../../../gds/Input";
import SelectBox from "../../../../gds/SelectBox";
import CheckboxButtonGroup, { LayoutTypes } from "../../../../gds/CheckboxButtonGroup";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import PrincipalFlowVerticalStepperElements from "../../../constants/PrincipalFlowVerticalStepperElements";
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import useTitle from "../../../../../hooks/useTitle";
import useInterviewUpdater from "../../../hooks/useInterviewUpdater";
import useMappers from "./useMappers";
import { validationSchema } from "./validation";
import useFormButtons from "../../../hooks/useFormButtons";
import useGtmTracking from "../../../../../hooks/useGtmTracking";

export default function PrincipalApplicationSubmitted() {
    useTitle("Application Submitted");

    useGtmTracking("Onboarding:PrincipalCongrats", "onboarding_principal_complete");

    const [formData, setFormData] = useState();

    const { defaultValues, agencyAnswers } = useMappers(formData);

    const { register, handleSubmit, watch, control, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues
    });

    const {
        pageStatus,
        onSubmit
    } = useFormButtons(setFormData);

    const {
        updatingInterviews,
        interviewsUpdated,
        updateErrors
    } = useInterviewUpdater(
        "PrincipalApplicationSubmitted",
        pageStatus,
        null,
        agencyAnswers
    );

    const [disableSubmitButton, setDisableSubmitButton] = useState(false);

    const yesNoOptions = useYesNoOptions();
    const referralOptions = useReferralOptions();

    const watchIncentiveParticipation = watch("incentiveParticipation");
    const watchIncentiveContact = watch("incentiveContact");
    const watchReferralSource = watch("referralSource");

    const successToastRef = useRef();
    const errorToastRef = useRef();

    useEffect(() => {
        if (interviewsUpdated) {
            successToastRef.current?.setAttribute?.("visible", "");
        }
    }, [interviewsUpdated]);

    useEffect(() => {
        if (updateErrors) {
            errorToastRef.current?.setAttribute?.("visible", "");
        }
    }, [updateErrors]);

    useEffect(() => {
        setDisableSubmitButton(
            updatingInterviews ||
            !watchIncentiveParticipation ||
            !watchReferralSource ||
            (watchIncentiveParticipation !== "false" &&
                (
                    !watchIncentiveContact ||
                    watchIncentiveContact.length < 1
                )
            ))
    }, [updatingInterviews, watchIncentiveParticipation, watchIncentiveContact, watchReferralSource])

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Application Submitted"
            header="Application Submitted"
        >
            <gds-toast data-onboarding-control-id="PrincipalFlow:ApplicationSubmitted:PrincipalApplicationSubmitted:MarketingInfoSubmitted"
                ref={successToastRef}
                type="success"
            >
                Incentives & Marketing Info Submitted!
            </gds-toast>

            <gds-toast data-onboarding-control-id="PrincipalFlow:ApplicationSubmitted:PrincipalApplicationSubmitted:MarketingInfoError"
                ref={errorToastRef}
                type="informational"
            >
                An unexpected error occurred. Please try again.
            </gds-toast>

            <h3 className="mb-5">Thank you for applying to be an agent with GEICO!</h3>
            <div className="panel-content">
                <p className="mb-5">
                    Your application has been submitted. We'll review your details and contact you when you've been approved to start selling with GEICO. This typically only takes a few days.
                </p>
            </div>

            {!interviewsUpdated && (
                <div className="panel-content">
                    <h4>Incentives & Marketing</h4>
                    <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                        <RadioButtonGroup trackingId="PrincipalFlow:ApplicationSubmitted:PrincipalApplicationSubmitted:IncentiveParticipation"
                            control={control}
                            name="incentiveParticipation"
                            legend={<>
                                Would you like your agency to participate in incentives and rewards programs?{/*
                                */}<span className='required'>*</span>
                            </>}
                            layout="Two Column"
                            options={yesNoOptions}
                        />

                        <div style={{ display: watchIncentiveParticipation === "true" ? "block" : "none" }}>
                            <CheckboxButtonGroup trackingId="PrincipalFlow:ApplicationSubmitted:PrincipalApplicationSubmitted:IncentiveContact"
                                control={control}
                                name="incentiveContact"
                                legend="Who should be contacted about incentive programs?"
                                layout={LayoutTypes.TwoColumn}
                                options={[
                                    { value: "AGY", label: "Principal/Owner", trackingId: "PrincipalOwner" },
                                    { value: "PROD", label: "Producers", trackingId: "Producers" }
                                ]}
                            />
                        </div>

                        <SelectBox
                            control={control}
                            name="referralSource"
                            label={<>
                                How did you hear about GEICO for your agency?{/*
                                */}<span className='required'>*</span>
                            </>}
                            options={referralOptions}
                            placeholder="Please select..."
                        />

                        <div className="flex-row">
                            <Input
                                input-id="agency-website"
                                placeholder="Agency Website Address"
                                full-width
                                {...register("agencyWebsite")}
                            >
                                Agency Website Address
                                {errors.agencyWebsite &&
                                    <span slot="error-message-copy">{errors.agencyWebsite.message}</span>
                                }
                            </Input>

                            <Input
                                input-id="agency-facebook"
                                placeholder="Agency Facebook Handle"
                                full-width
                                {...register("agencyFacebook")}
                            >
                                Agency Facebook Handle
                                {errors.agencyFacebook &&
                                    <span slot="error-message-copy">{errors.agencyFacebook.message}</span>
                                }
                            </Input>
                        </div>

                        <div className="flex-row">
                            <Input
                                input-id="agency-instagram"
                                placeholder="Agency Instagram Handle"
                                full-width
                                {...register("agencyInstagram")}
                            >
                                Agency Instagram Handle
                                {errors.agencyInstagram &&
                                    <span slot="error-message-copy">{errors.agencyInstagram.message}</span>
                                }
                            </Input>
                        </div>

                        <p>
                            <span className='required'>*</span> Indicates required field.
                        </p>

                        <div className="margin-top">
                            <button data-onboarding-button-id="PrincipalFlow:ApplicationSubmitted:PrincipalApplicationSubmitted:SubmitMarketingInfo" id="btnSubmitPrincipalApplication" type="submit" className="btn btn--primary btn--pull-right" disabled={disableSubmitButton}>
                                <span>Submit</span>
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </GeaoVerticalStepperLayout>
    );
}